import React, { useMemo, useState } from "react";
import { t } from "ttag";
import Picker from "react-mobile-picker";
import { ChevronDown } from "@coolremote/shared";
import {
  bacnetUnits,
  bacnetUnitTypes,
  getBacnetObjectTypeFromShortId,
} from "@components/AddEditBacnetUiTemplateDialog/constants";
import styles from "./BacnetUnitControl.module.css";

export interface IBacnetUnitPoint {
  objectId: string;
  name: string;
  shortId?: string;
  readOnly?: boolean;
  units?: string | number | null;
  userUnits?: string | number | null;
  minValue?: number | null;
  maxValue?: number | null;
  step?: number | null;
  customEnum?: Record<string, string> | any;
  customEnumVisibility?: Record<string, boolean>;
  property?: string;
}

export interface IBacnetUnitControlProps {
  unitName: string;
  points: IBacnetUnitPoint[];
  operationalData?: Record<string, any>;
  readOnly?: boolean;
  onSetValue?: (objectId: string, value: number) => void;
}

export const getBacnetUnitSymbol = (unitIndexOrName: any): string => {
  if (unitIndexOrName == null) {
    return "";
  }
  if (typeof unitIndexOrName === "number") {
    return (bacnetUnits as any)[unitIndexOrName]?.symbol || "";
  }
  if (typeof unitIndexOrName === "string") {
    const parsed = parseInt(unitIndexOrName, 10);
    if (!isNaN(parsed) && (bacnetUnits as any)[parsed]) {
      return (bacnetUnits as any)[parsed]?.symbol || "";
    }
    return unitIndexOrName;
  }
  return "";
};

export const getBacnetPointRawValue = (
  point: IBacnetUnitPoint,
  operationalData: Record<string, any> = {}
): any => {
  const objectId = point.objectId;
  const shortId = point.shortId;
  const candidates = [
    objectId != null ? `${objectId}/85` : null,
    objectId,
    shortId != null ? `${shortId}/85` : null,
    shortId,
  ].filter(Boolean) as string[];

  for (const key of candidates) {
    const v = operationalData[key];
    if (v !== undefined && v !== null) {
      return v;
    }
  }
  return null;
};

const getBacnetObjectTypeCode = (point: IBacnetUnitPoint): number | null =>
  getBacnetObjectTypeFromShortId(point.shortId || point.objectId || "");

const MULTI_STATE_INPUT = 13;

const isBinaryPoint = (point: IBacnetUnitPoint): boolean => {
  const type = getBacnetObjectTypeCode(point);
  return (
    type === bacnetUnitTypes.BI ||
    type === bacnetUnitTypes.BO ||
    type === bacnetUnitTypes.BV
  );
};

const getEnumEntries = (point: IBacnetUnitPoint): [string, string][] => {
  if (!point.customEnum || typeof point.customEnum !== "object") { 
    return [];
  }
  return Object.entries(point.customEnum).filter(
    ([k]) => k !== "min" && k !== "max" && k !== "step"
  ) as [string, string][];
};

const isInputOnly = (point: IBacnetUnitPoint): boolean => {
  const type = getBacnetObjectTypeCode(point);
  return (
    type === bacnetUnitTypes.AI ||
    type === bacnetUnitTypes.BI ||
    type === MULTI_STATE_INPUT
  );
};

// ─── Wheel picker row (list + numeric) ───────────────────────────────────────

interface IWheelRowProps {
  label: string;
  displayValue: string;
  options: string[];
  selectedOption: string;
  readOnly?: boolean;
  onSelect: (option: string) => void;
}

const WheelRow: React.FC<IWheelRowProps> = ({
  label,
  displayValue,
  options,
  selectedOption,
  readOnly = false,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    if (!readOnly) setOpen((v) => !v);
  };

  return (
    <li className={readOnly ? styles.rowReadOnly : styles.pickerContainer}>
      <div className={styles.pickerHeader} onClick={handleToggle}>
        <span className={styles.rowLabel}>{label}</span>
        <div className={styles.rowRight}>
          <span className={styles.valueText}>{displayValue}</span>
          {!readOnly && (
            <ChevronDown
              className={styles.chevron}
              style={open ? { transform: "rotate(180deg)" } : undefined}
            />
          )}
        </div>
      </div>
      {open && !readOnly && (
        <div className={styles.pickerWheelWrapper}>
          <Picker
            optionGroups={{ value: options }}
            valueGroups={{ value: selectedOption }}
            onChange={(_name: string, val: string) => onSelect(val)}
            wheel="normal"
            height={150}
            itemHeight={30}
          />
        </div>
      )}
    </li>
  );
};

// ─── Numeric wheel row ───────────────────────────────────────────────────────

interface INumericWheelRowProps {
  point: IBacnetUnitPoint;
  value: number | null;
  displayValue: string;
  activePickerId: string | null;
  onPickerToggle: (id: string) => void;
  rowKey: string;
  onSetValue: (v: number) => void;
}

const MAX_WHEEL_ITEMS = 500;

const NumericWheelRow: React.FC<INumericWheelRowProps> = ({
  point,
  value,
  displayValue,
  activePickerId,
  onPickerToggle,
  rowKey,
  onSetValue,
}) => {
  const isOpen = activePickerId === rowKey;
  const symbol = getBacnetUnitSymbol(point.userUnits ?? point.units);

  const { min, max, step } = useMemo(() => {
    const custom = point.customEnum || {};
    const minVal = point.minValue ?? (custom.min != null ? parseFloat(custom.min) : 0);
    const maxVal = point.maxValue ?? (custom.max != null ? parseFloat(custom.max) : 100);
    const stepVal = point.step ?? (custom.step != null ? parseFloat(custom.step) : 1);
    return { min: minVal, max: maxVal, step: stepVal > 0 ? stepVal : 1 };
  }, [point.minValue, point.maxValue, point.step, point.customEnum]);

  const { options, closestOption } = useMemo(() => {
    const count = Math.round((max - min) / step) + 1;
    if (count > MAX_WHEEL_ITEMS || isNaN(count) || count <= 0) {
      return { options: null, closestOption: "" };
    }

    const opts: string[] = [];
    for (let v = min; v <= max; v += step) {
      opts.push(`${parseFloat(v.toFixed(10))}${symbol ? ` ${symbol}` : ""}`);
    }

    const numVal = value ?? min;
    const closest =
      opts.find((o) => parseFloat(o) === numVal) ||
      opts.reduce((prev, curr) =>
        Math.abs(parseFloat(curr) - numVal) < Math.abs(parseFloat(prev) - numVal)
          ? curr
          : prev
      );

    return { options: opts, closestOption: closest };
  }, [min, max, step, symbol, value]);

  if (!options) {
    return (
      <StepperRow
        point={point}
        value={value}
        displayValue={displayValue}
        min={min}
        max={max}
        step={step}
        onSetValue={onSetValue}
      />
    );
  }

  return (
    <li className={styles.pickerContainer}>
      <div className={styles.pickerHeader} onClick={() => onPickerToggle(rowKey)}>
        <span className={styles.rowLabel}>{point.name}</span>
        <div className={styles.rowRight}>
          <span className={styles.valueText}>{displayValue}</span>
          <ChevronDown
            className={styles.chevron}
            style={isOpen ? { transform: "rotate(180deg)" } : undefined}
          />
        </div>
      </div>
      {isOpen && (
        <div className={styles.pickerWheelWrapper}>
          <Picker
            optionGroups={{ value: options }}
            valueGroups={{ value: closestOption }}
            onChange={(_name: string, opt: string) => {
              const num = parseFloat(opt.split(" ")[0]);
              if (!isNaN(num)) onSetValue(num);
            }}
            wheel="normal"
            height={150}
            itemHeight={30}
          />
        </div>
      )}
    </li>
  );
};

// ─── Stepper fallback for unbounded numeric ranges ────────────────────────────

interface IStepperRowProps {
  point: IBacnetUnitPoint;
  value: number | null;
  displayValue: string;
  min: number | null;
  max: number | null;
  step: number;
  onSetValue: (v: number) => void;
}

const StepperRow: React.FC<IStepperRowProps> = ({
  point,
  value,
  displayValue,
  min,
  max,
  step,
  onSetValue,
}) => {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const current = value ?? 0;

  const clamp = (v: number) => {
    if (min != null && v < min) {
      return min;
    }
    if (max != null && v > max) {
      return max;
    }
    return v;
  };
  const round = (v: number) => parseFloat(v.toFixed(10));

  const canDown = min == null || current - step >= min;
  const canUp = max == null || current + step <= max;

  const commitInput = () => {
    const n = parseFloat(inputVal);
    if (!isNaN(n)) onSetValue(round(clamp(n)));
    setEditing(false);
  };

  return (
    <li className={styles.pickerContainer}>
      <div className={styles.pickerHeader}>
        <span className={styles.rowLabel}>{point.name}</span>
        <div className={styles.rowRight}>
          <div className={styles.numericStepper}>
            <button
              className={styles.stepBtn}
              onClick={() => onSetValue(round(clamp(current - step)))}
              disabled={!canDown}
            >
              −
            </button>
            {editing ? (
              <input
                autoFocus
                type="number"
                className={styles.stepInput}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onBlur={commitInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitInput();
                  if (e.key === "Escape") setEditing(false);
                }}
              />
            ) : (
              <span
                className={styles.stepValue}
                title={t`Click to type a value`}
                onClick={() => {
                  setInputVal(value != null ? String(value) : "");
                  setEditing(true);
                }}
              >
                {displayValue}
              </span>
            )}
            <button
              className={styles.stepBtn}
              onClick={() => onSetValue(round(clamp(current + step)))}
              disabled={!canUp}
            >
              +
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

// ─── Main BacnetUnitControl Component ─────────────────────────────────────────

const BacnetUnitControl: React.FC<IBacnetUnitControlProps> = ({
  points = [],
  operationalData = {},
  readOnly = false,
  onSetValue,
}) => {
  const [activePickerId, setActivePickerId] = useState<string | null>(null);

  const getDisplayText = (point: IBacnetUnitPoint, rawVal: any): string => {
    if (rawVal === null || rawVal === undefined) {
      return "—";
    }
    const symbol = getBacnetUnitSymbol(point.userUnits ?? point.units);

    // Custom enum mapping
    if (point.customEnum && typeof point.customEnum === "object") {
      const strVal = String(rawVal);
      if (point.customEnum[strVal] != null) {
        return point.customEnum[strVal];
      }
      const num = Number(rawVal);
      if (!isNaN(num) && point.customEnum[String(Math.round(num))] != null) {
        return point.customEnum[String(Math.round(num))];
      }
    }

    if (isBinaryPoint(point)) {
      const num = Number(rawVal);
      const isTrue = !isNaN(num)
        ? Math.round(num) === 1
        : rawVal === true || rawVal === "1" || rawVal === "ON" || rawVal === "on";
      return isTrue ? t`On` : t`Off`;
    }

    const n = Number(rawVal);
    if (isNaN(n)) {
      return `${rawVal}${symbol ? ` ${symbol}` : ""}`;
    }
    const formatted = Number.isInteger(n) ? String(n) : n.toFixed(1);
    return `${formatted}${symbol ? ` ${symbol}` : ""}`;
  };

  const renderPointRow = (point: IBacnetUnitPoint, index: number) => {
    const rawVal = getBacnetPointRawValue(point, operationalData);
    const isRowReadOnly = readOnly || point.readOnly || isInputOnly(point);
    const rowKey = `${point.objectId || index}`;
    const displayVal = getDisplayText(point, rawVal);

    // ── Read-only row ─────────────────────────────────────────────────────────
    if (isRowReadOnly) {
      return (
        <li key={rowKey} className={styles.rowReadOnly}>
          <div className={styles.pickerHeader}>
            <span className={styles.rowLabel}>{point.name}</span>
            <div className={styles.rowRight}>
              <span className={styles.valueText}>{displayVal}</span>
            </div>
          </div>
        </li>
      );
    }

    const enumEntries = getEnumEntries(point);
    if (enumEntries.length > 0) {
      const visibleEntries = point.customEnumVisibility
        ? enumEntries.filter(([k]) => point.customEnumVisibility?.[k] !== false)
        : enumEntries;

      if (!visibleEntries.length) {
        return (
          <li key={rowKey} className={styles.rowReadOnly}>
            <div className={styles.pickerHeader}>
              <span className={styles.rowLabel}>{point.name}</span>
              <div className={styles.rowRight}>
                <span className={styles.valueText}>{displayVal}</span>
              </div>
            </div>
          </li>
        );
      }

      const options = visibleEntries.map(([, label]) => label);
      const selectedKey =
        rawVal != null ? String(Math.round(Number(rawVal))) : visibleEntries[0][0];
      const selected =
        point.customEnum?.[selectedKey] ?? options[0] ?? visibleEntries[0][1];

      return (
        <WheelRow
          key={rowKey}
          label={point.name}
          displayValue={displayVal}
          options={options}
          selectedOption={selected}
          onSelect={(opt) => {
            const found = enumEntries.find(([, v]) => v === opt);
            if (found) onSetValue?.(point.objectId, Number(found[0]));
          }}
        />
      );
    }

    // ── Numeric point ─────────────────────────────────────────────────────────
    const numVal = rawVal != null ? Number(rawVal) : null;
    return (
      <NumericWheelRow
        key={rowKey}
        point={point}
        value={!isNaN(numVal as number) ? numVal : null}
        displayValue={displayVal}
        activePickerId={activePickerId}
        onPickerToggle={(id) =>
          setActivePickerId((prev) => (prev === id ? null : id))
        }
        rowKey={rowKey}
        onSetValue={(v) => onSetValue?.(point.objectId, v)}
      />
    );
  };

  return (
    <div className={styles.card}>
      {points.length === 0 ? (
        <div className={styles.empty}>{t`No parameters configured.`}</div>
      ) : (
        <ul className={styles.rowList}>
          {points.map((point, index) => renderPointRow(point, index))}
        </ul>
      )}
    </div>
  );
};

export default BacnetUnitControl;
export { BacnetUnitControl };
